import { ingestEvents } from '@/atoms/stats'
import {
  getReplaceableCoordinateFromEvent,
  getRoot,
  isReplaceableEvent,
  isProtectedEvent
} from '@/lib/event'
import { toNote } from '@/lib/link'
import { useSecondaryPage } from '@/PageManager'
import { mutePubkeySetAtom } from '@/atoms/lists'
import { useAtomValue } from 'jotai'
import { repliesMapAtom, addReplies } from '@/atoms/replies'
import client from '@/services/client.service'
import { Event as NEvent } from '@nostr/tools/wasm'
import * as kinds from '@nostr/tools/kinds'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { LoadingBar } from './LoadingBar'
import PostEditor from './PostEditor'
import ReplyNote, { ReplyNoteSkeleton } from './ReplyNote'
import { SubCloser } from '@nostr/tools/abstract-pool'
import { TFeedSubRequest } from '@/types'
import { loadRelayList } from '@nostr/gadgets/lists'
import { AddressPointer, EventPointer } from '@nostr/tools/nip19'
import { useAsync } from 'react-use'
import { NostrEvent } from '@nostr/tools/core'
import { canWriteAtom, useCurrentPubkey } from '@/atoms/session'

const LIMIT = 100
const SHOW_COUNT = 20

export function findAuthorChain(repliesMap: Map<string, NEvent[]>, event: NEvent) {
  if (event.kind !== kinds.ShortTextNote) return []
  const chain: NEvent[] = []
  let currentKey = getEventKey(event)
  while (true) {
    const children = repliesMap.get(currentKey) || []
    const authorReply = children.find((c) => c.pubkey === event.pubkey)
    if (!authorReply) break
    if (authorReply.kind !== kinds.ShortTextNote) break
    chain.push(authorReply)
    currentKey = getEventKey(authorReply)
  }
  return chain
}

export default function ReplyNoteList({
  index,
  event,
  selectedRelays
}: {
  index?: number
  event: NEvent
  selectedRelays?: string[]
}) {
  const { t } = useTranslation()
  const { push, currentIndex } = useSecondaryPage()
  const mutePubkeySet = useAtomValue(mutePubkeySetAtom)
  const [subRequests, setSubRequests] = useState<null | TFeedSubRequest[]>(null)
  const repliesMap = useAtomValue(repliesMapAtom)
  const pubkey = useCurrentPubkey()
  const canWrite = useAtomValue(canWriteAtom)
  const replies = useMemo(() => {
    const replyKeySet = new Set<string>()
    const replyEvents: NostrEvent[] = []
    const currentEventKey = getEventKey(event)
    let parentEventKeys = [currentEventKey]

    while (parentEventKeys.length > 0) {
      const events = parentEventKeys.flatMap((key) => repliesMap.get(key) || [])
      for (const evt of events) {
        const key = getEventKey(evt)
        if (replyKeySet.has(key)) continue
        if (mutePubkeySet.has(evt.pubkey)) continue
        replyKeySet.add(key)
        replyEvents.push(evt)
      }

      parentEventKeys = events.map((evt) => getEventKey(evt))
    }
    const chainKeys = new Set(findAuthorChain(repliesMap, event).map(getEventKey))
    return replyEvents
      .filter((e) => !chainKeys.has(getEventKey(e)))
      .sort((a, b) => a.created_at - b.created_at)
  }, [event.id, repliesMap, mutePubkeySet])

  const [until, setUntil] = useState<number | undefined>(undefined)
  const [loading, setLoading] = useState<boolean>(false)
  const [showCount, setShowCount] = useState(SHOW_COUNT)
  const [highlightReplyKey, setHighlightReplyKey] = useState<EventPointer | undefined>(undefined)
  const [bulkReplyMode, setBulkReplyMode] = useState(false)
  const [selectedEventIds, setSelectedEventIds] = useState<string[]>([])
  const [bulkPostEditorOpen, setBulkPostEditorOpen] = useState(false)
  const replyRefs = useRef<{ [id: string]: HTMLDivElement | null }>({})
  const bottomRef = useRef<HTMLDivElement | null>(null)

  useAsync(async () => {
    const rootPointer = await getRoot(event)
    const root = rootPointer
      ? 'value' in rootPointer
        ? undefined
        : await client.fetchEvent(rootPointer)
      : event
    const requests: TFeedSubRequest[] = []

    let relays: string[] = []
    if (window.hallway.universe.readRepliesFromExclusive) {
      relays = window.hallway.universe.readRepliesFrom || []
    } else {
      if (pubkey) {
        const userRelays = await loadRelayList(pubkey)
        relays.push(
          ...userRelays.items
            .filter((r) => r.read)
            .map((r) => r.url)
            .slice(0, 2)
        )
      }
      if (root) {
        const rootRelays = await loadRelayList(root.pubkey)
        relays.push(
          ...rootRelays.items
            .filter((r) => r.read)
            .map((r) => r.url)
            .slice(0, 2)
        )
      }
      if (isProtectedEvent(event)) {
        const seenOn = client.getSeenEventRelays(event.id)
        relays.push(...seenOn.map((r) => r.url).slice(0, 2))
      }
      relays.push(...(window.hallway.universe.readRepliesFrom || []))
    }
    relays = relays.slice(0, 5)

    // get all direct replies to this
    if (
      !isReplaceableEvent(
        event.kind
      ) /* if this is a replaceable then it will be a root regardless so we don't have to fetch stuff here */
    ) {
      requests.push({
        source: 'relays',
        urls: selectedRelays
          ? selectedRelays
          : window.hallway.universe.readRepliesFromExclusive
            ? relays
            : relays.concat(
                (await loadRelayList(event.pubkey)).items
                  .filter((r) => r.read)
                  .map((r) => r.url)
                  .slice(0, 2)
              ),
        filter: {
          '#e': [event.id],
          kinds: [kinds.ShortTextNote, kinds.Comment, kinds.VoiceComment]
        }
      })
    }

    // get quotes to the currently focused event
    requests.push({
      source: 'relays',
      urls: relays,
      filter: {
        '#q': [
          kinds.isReplaceableKind(event.kind) ? getReplaceableCoordinateFromEvent(event) : event.id
        ],
        kinds: [kinds.ShortTextNote, kinds.Highlights, kinds.LongFormArticle, kinds.Comment]
      }
    })

    // get all comments/replies to the thread root, even when a non-root reply is focused.
    // for kind:1 roots, the `#e` filter returns every reply in the thread (NIP-10 marks
    // the root on every nested `e` tag), which gives the BFS everything it needs to
    // surface grandchildren of the focused reply.
    if (root) {
      if (isReplaceableEvent(root.kind)) {
        requests.push({
          source: 'relays',
          urls: relays,
          filter: {
            '#A': [getReplaceableCoordinateFromEvent(root)],
            kinds: [kinds.Comment, kinds.VoiceComment]
          }
        })
      }

      requests.push(
        {
          source: 'relays',
          urls: selectedRelays
            ? selectedRelays
            : window.hallway.universe.readRepliesFromExclusive
              ? relays
              : relays.concat(
                  (await loadRelayList(root.pubkey)).items.filter((r) => r.read).map((r) => r.url)
                ),
          filter: {
            '#e': [root.id],
            kinds: [kinds.ShortTextNote, kinds.Comment, kinds.VoiceComment]
          }
        },
        {
          source: 'relays',
          urls: relays,
          filter: {
            '#E': [root.id],
            kinds: [kinds.Comment, kinds.VoiceComment]
          }
        }
      )
    } else if (rootPointer && 'value' in rootPointer) {
      requests.push({
        source: 'relays',
        urls: relays,
        filter: {
          '#I': [rootPointer.value],
          kinds: [kinds.Comment, kinds.VoiceComment]
        }
      })
    }

    setSubRequests(
      requests.flatMap<TFeedSubRequest>((subReq) => [
        subReq,
        ...(!selectedRelays // (when selectedRelays exists we won't read from local store)
          ? [
              {
                ...subReq,
                source: 'local'
              } as TFeedSubRequest
            ]
          : [])
      ])
    )
  }, [event, selectedRelays])

  useEffect(() => {
    if (loading || !subRequests || subRequests.length === 0 || currentIndex !== index) return

    setLoading(true)
    let isClosed = false

    // timeout fallback in case relays don't respond
    const timeoutId = setTimeout(() => {
      if (!isClosed) {
        setLoading(false)
      }
    }, 10000)

    let subc: SubCloser | undefined
    try {
      subc = client.subscribeTimeline(
        subRequests,
        {
          limit: LIMIT
        },
        {
          onEvents: (events, isFinal) => {
            if (isFinal) {
              isClosed = true
              clearTimeout(timeoutId)
              setUntil(
                events.length >= LIMIT ? events[events.length - 1].created_at - 1 : undefined
              )
              setLoading(false)
            }

            if (events.length > 0) {
              ingestEvents(events)
              addReplies(events)
            }
          },
          onNew: (evt) => {
            ingestEvents([evt])
            addReplies([evt])
          }
        }
      )
    } catch (_err) {
      clearTimeout(timeoutId)
      setLoading(false)
    }

    return () => {
      clearTimeout(timeoutId)
      subc?.close?.()
    }
  }, [subRequests, currentIndex, index])

  useEffect(() => {
    if (replies.length === 0) {
      loadMore()
    }
  }, [replies])

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '10px',
      threshold: 0.1
    }

    const observerInstance = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setShowCount((prev) => (prev < replies.length ? prev + SHOW_COUNT : prev))
      }
    }, options)

    const currentBottomRef = bottomRef.current

    if (currentBottomRef) {
      observerInstance.observe(currentBottomRef)
    }

    return () => {
      if (observerInstance && currentBottomRef) {
        observerInstance.unobserve(currentBottomRef)
      }
    }
  }, [replies])

  const selectedEvents = useMemo(() => {
    const ids = new Set(selectedEventIds)
    return replies.filter((r) => ids.has(getEventKey(r)))
  }, [replies, selectedEventIds])

  return (
    <div className="min-h-[80vh]">
      {loading && <LoadingBar />}
      {!loading && until && until > event.created_at && (
        <div
          className={`text-sm text-center text-muted-foreground border-b py-2 ${!loading ? 'hover:text-foreground cursor-pointer' : ''}`}
          onClick={loadMore}
        >
          {t('load more older replies')}
        </div>
      )}
      <div>
        {replies.slice(0, showCount).map((reply) => {
          const isSelected = selectedEventIds.includes(reply.id)

          return (
            <div
              ref={(el) => (replyRefs.current[reply.id] = el)}
              key={reply.id}
              className={`scroll-mt-12 ${isSelected && bulkReplyMode ? 'bg-primary/10' : ''}`}
            >
              <ReplyNote
                event={reply}
                onClickParent={(parent: EventPointer | AddressPointer) => {
                  if ('identifier' in parent) return // an address can never be a reply so we can ignore this
                  highlightReply(parent)
                }}
                highlight={highlightReplyKey?.id === reply.id}
                simpleEmbedId={event.id}
                bulkReplyMode={bulkReplyMode}
                isSelected={isSelected}
                onToggleSelect={() => toggleBulkSelect(reply.id)}
                onBulkReply={canWrite ? () => enterBulkReply(reply) : undefined}
              />
            </div>
          )
        })}
      </div>
      {!loading && (
        <div className="text-sm mt-2 mb-3 text-center text-muted-foreground">
          {replies.length > 0 ? t('no more replies') : t('no replies')}
        </div>
      )}
      <div ref={bottomRef} />
      {loading && <ReplyNoteSkeleton />}

      {bulkReplyMode && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 rounded-lg border bg-background px-4 py-3 shadow-lg">
          <span className="text-sm font-medium whitespace-nowrap">
            {selectedEventIds.length} {t('selected')}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="text-primary"
            onClick={() => {
              cancelBulkReply()
            }}
          >
            {t('Cancel')}
          </Button>
          <Button
            size="sm"
            onClick={() => {
              if (selectedEventIds.length === 0) return
              setBulkPostEditorOpen(true)
            }}
            disabled={selectedEventIds.length === 0 || !canWrite}
          >
            {t('Reply')}
          </Button>
        </div>
      )}

      <PostEditor
        parentEvents={selectedEvents}
        open={bulkPostEditorOpen}
        setOpen={(open) => {
          setBulkPostEditorOpen(open)
          if (!open) cancelBulkReply()
        }}
      />
    </div>
  )

  async function loadMore() {
    if (!subRequests || loading || !until) return

    setLoading(true)
    try {
      const events = await client.loadMoreTimeline(subRequests, { until, limit: LIMIT })
      addReplies(events)
      setUntil(events.length ? events[events.length - 1].created_at - 1 : undefined)
    } catch (_err) {
      // Failed to load more, but don't block UI
    } finally {
      setLoading(false)
    }
  }

  function highlightReply(reply: EventPointer, scrollTo = true) {
    let found = false
    if (scrollTo) {
      const ref = replyRefs.current[reply.id]
      if (ref) {
        found = true
        ref.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    }
    if (!found) {
      if (reply) push(toNote(reply))
      return
    }

    setHighlightReplyKey(reply)
    setTimeout(() => {
      setHighlightReplyKey((pre) => (pre?.id === reply.id ? undefined : pre))
    }, 1500)
  }

  function enterBulkReply(reply: NostrEvent) {
    setBulkReplyMode(true)
    setSelectedEventIds([getEventKey(reply)])
  }

  function toggleBulkSelect(replyKey: string) {
    setSelectedEventIds((prev) => {
      const set = new Set(prev)
      if (set.has(replyKey)) set.delete(replyKey)
      else set.add(replyKey)
      return Array.from(set)
    })
  }

  function cancelBulkReply() {
    setBulkReplyMode(false)
    setSelectedEventIds([])
  }
}

// for internal identification of events
function getEventKey(event: NostrEvent) {
  return isReplaceableEvent(event.kind) ? getReplaceableCoordinateFromEvent(event) : event.id
}
